import styles from "./addtaskform.module.css";
import { Autocomplete, Button, FormControl, InputLabel, MenuItem, Select, TextField } from "@mui/material";
import { useEffect, useState } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useAuth0 } from "@auth0/auth0-react";
import axios from "axios";
import { AddLogCallAPI } from "../../../components/AddLogs";
import { toast } from 'react-smart-toaster';
import FrequencySelector from "./FrequencySelector";
import TaskQuestions from "./TaskQuestions";

const taskSchema = {
    task_name: yup.string().required("This field is Required"),
};

const schema = yup.object().shape({
    title: yup.string().required("This field is Required"),
    description: yup.string(),
    property_id: yup.string(),
    technician: yup.string().required("This field is Required"),
    secondary_technician: yup.string(),
    taskList: yup.array().of(yup.object().shape(taskSchema)),
});

const AddTaskForm = ({ setAddTaskModalOpen, setLoading, setSnackbarOpen, setSnackbarMsg, addTaskLocal }) => {
    const { getAccessTokenSilently } = useAuth0();
    const [propertyList, setPropertyList] = useState([]);
    const [allTechnicianList, setAllTechnicianList] = useState([]);
    const [technicianList, setTechnicianList] = useState([]);
    const [secondaryTechnicianList, setSecondaryTechnicianList] = useState([]);
    const [charsLeft, setCharsLeft] = useState({ description: 200 });
    const [frequency, setFrequency] = useState('Daily');
    const [selectedDays, setSelectedDays] = useState([]);
    const [selectedWeeks, setSelectedWeeks] = useState([]);
    const [selectedDates, setSelectedDates] = useState([]);

    const { handleSubmit, control, errors, setValue } = useForm({
        resolver: yupResolver(schema),
        mode: "onChange",
        defaultValues: {
            taskList: [{ task_name: "", question: "", question_type: "Text", is_required: 0, answer_options: [''] }],
            technician: '',
            secondary_technician: ''
        },
        shouldUnregister: false
    });

    const { fields, append, remove } = useFieldArray({ control, name: "taskList" });

    useEffect(() => {
        async function fetchPropertyList() {
            const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

            axios(process.env.REACT_APP_CASE_API_URL + "v1/property-list", {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }).then((response) => {
                setPropertyList(response.data.data);
            }).catch(() => { });
        }
        fetchPropertyList();
    }, [getAccessTokenSilently]);

    useEffect(() => {
        async function fetchPropertyList() {
            const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

            axios(process.env.REACT_APP_CASE_API_URL + "v1/all-user-list", {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }).then((response) => {
                setAllTechnicianList(response.data.data);
            }).catch(() => { });
        }
        fetchPropertyList();
    }, [getAccessTokenSilently]);

    const onSubmit = async (data) => {
        data = { ...data, frequency: frequency };

        if (frequency === 'Daily') {
            if (selectedDays.length === 0) {
                toast.error('Please select day(s)');
                return false;
            } else {
                data = { ...data, days: selectedDays.join(',') };
            }
        }

        if (frequency === 'Weekly') {
            if (selectedWeeks.length === 0) {
                toast.error('Please select week(s)');
                return false;
            } else if (selectedDays.length === 0) {
                toast.error('Please select day(s)');
                return false;
            } else {
                data = { ...data, weeks: selectedWeeks.join(','), days: selectedDays.join(',') };
            }
        }

        setLoading(true);
        setAddTaskModalOpen(false);

        const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });
        return axios.post(process.env.REACT_APP_CASE_API_URL + "v1/tasks", data, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        }).then(response => {
            setLoading(false);
            setSnackbarOpen(true);
            setSnackbarMsg(response.data.message);

            addTaskLocal(response.data.data);

            let logData = {
                'title': 'A new task list has been added',
                'description': [
                    'Name: ' + response?.data?.data?.title,
                    'Property: ' + response?.data?.data?.property_name,
                    'Technician: ' + response?.data?.data?.technician_name
                ]
            }
            AddLogCallAPI(logData, token);
        }).catch(error => {
            if (typeof error.response !== 'undefined')
                toast.error(error.response.data.message);
            setLoading(false);
        });
    }

    return (<form onSubmit={handleSubmit(onSubmit)}>
        <div className={`${styles.AddprospectFirstStepForm}`}>
            <div className={`${styles.FormArea}`}>
                <div className={`${styles.AddPropertyHalfSecRow}`}>
                    <div className={`${styles.HalfSecFull} FormGroup`}>
                        <Controller
                            name={`title`}
                            control={control}
                            render={(field) => (<TextField
                                {...field}
                                id="outlined-basic"
                                label="Task name*"
                            />)}
                        />
                        {errors.title && (<p className={`${styles.ErrorM}`}>{errors?.title?.message}</p>)}
                    </div>
                    <div className={`${styles.HalfSecFull} FormGroup`}>
                        <Controller
                            name={`description`}
                            control={control}
                            render={(field) => (<TextField
                                {...field}
                                id="outlined-basic"
                                label="Description"
                                multiline
                                rows={4}
                                onChange={(e) => {
                                    field.onChange(e);

                                    setCharsLeft((prev) => {
                                        return { ...prev, description: 200 - e.target.value.length };
                                    });
                                }}
                                inputProps={{ maxLength: 200 }}
                            />)}
                        />
                        <p className={`${styles.Example}`}>{charsLeft?.description > 1 ? charsLeft?.description + " characters remaining" : charsLeft?.description + " character remaining"}</p>
                        {errors.description && (<p className={`${styles.ErrorM}`}>{errors?.description?.message}</p>)}
                    </div>
                </div>
                <div className={`${styles.HalfSecFull} FormGroup`}>
                    <FormControl fullWidth>
                        <InputLabel id="demo-simple-select-label">Property</InputLabel>
                        <Controller
                            name="property_id"
                            control={control}
                            render={(field) => (
                                <Select
                                    {...field}
                                    labelId="demo-simple-select-label"
                                    id="demo-simple-select"
                                    label="Property"
                                    onChange={(e) => {
                                        field.onChange(e);

                                        let technicianListTemp = allTechnicianList?.filter(i => i?.properties?.indexOf(e?.target?.value) > -1);

                                        setTechnicianList(technicianListTemp);
                                        setSecondaryTechnicianList([]);
                                        setValue('technician', '');
                                        setValue('secondary_technician', '');
                                    }}
                                >
                                    {propertyList?.map((item) => {
                                        return (<MenuItem value={item.value}>{item.label}</MenuItem>);
                                    })}
                                </Select>
                            )}
                        />
                    </FormControl>
                    {errors.property_id && (<p className={`${styles.ErrorM}`}>{errors?.property_id?.message}</p>)}
                </div>
                <div className={`${styles.FormGroupSec}`}>
                    <div className={`${styles.HalfSecFull} FormGroup`}>
                        <Controller
                            name="technician"
                            control={control}
                            render={(field) => (<>
                                <Autocomplete
                                    options={technicianList}
                                    getOptionLabel={(option) => option?.name || ""}
                                    value={technicianList.find((t) => t.id === field?.value) || null}
                                    noOptionsText={technicianList.length === 0 ? "Select Property First" : "No Technicians Available"}
                                    onChange={(_, value) => {
                                        field.onChange(value ? value.id : "");

                                        const secondaryTechnicianTemp = technicianList.filter((i) => i.id !== value?.id);

                                        setSecondaryTechnicianList(secondaryTechnicianTemp);
                                        setValue("secondary_technician", "");
                                    }}
                                    componentsProps={{
                                        paper: { sx: { "& .MuiAutocomplete-option": { fontSize: "14px" } } }
                                    }}
                                    renderInput={(params) => (<TextField {...params} label="Primary Technician*" />)}
                                    fullWidth
                                />
                            </>)}
                        />
                        {errors.technician && (<p className={`${styles.ErrorM}`}>{errors.technician.message}</p>)}
                    </div>

                    <div className={`${styles.HalfSecFull} FormGroup`}>
                        <Controller
                            name="secondary_technician"
                            control={control}
                            render={(field) => (<>
                                <Autocomplete
                                    options={secondaryTechnicianList}
                                    getOptionLabel={(option) => option?.name || ""}
                                    value={secondaryTechnicianList.find((t) => t.id === field.value) || null}
                                    noOptionsText={(technicianList.length === 0 ? "Select Property First" : secondaryTechnicianList.length === 0 ? "Select Primary Tenant First" : "No Technicians Available")}
                                    onChange={(_, value) => { field.onChange(value ? value.id : ""); }}
                                    componentsProps={{
                                        paper: { sx: { "& .MuiAutocomplete-option": { fontSize: "14px" } } }
                                    }}
                                    renderInput={(params) => (<TextField {...params} label="Secondary Technician" />)}
                                    fullWidth
                                />
                            </>)}
                        />
                        {errors.secondary_technician && (<p className={`${styles.ErrorM}`}>{errors.secondary_technician.message}</p>)}
                    </div>
                    <p className={`${styles.GroupHelpText}`}>This checklist can be accessed in the Management App by users who have permission.</p>
                </div>
                

                <FrequencySelector frequency={frequency} setFrequency={setFrequency.bind(this)} selectedDays={selectedDays} setSelectedDays={setSelectedDays.bind(this)} selectedWeeks={selectedWeeks} setSelectedWeeks={setSelectedWeeks.bind(this)} selectedDates={selectedDates} setSelectedDates={setSelectedDates.bind(this)} />
            </div>
            <div className={`${styles.ChecklistArea}`}>
                <TaskQuestions fields={fields} append={append.bind(this)} remove={remove.bind(this)} control={control} errors={errors} setValue={setValue.bind(this)} prevTaskList={[]} />
            </div>
            <div className={`${styles.ButtonAction}`}>
                <Button onClick={(e) => setAddTaskModalOpen(false)} className="CancelPopupBU">Cancel</Button>
                <Button className="SubmitPopupBU" type="submit">Add</Button>
            </div>
        </div>
    </form>)
}

export default AddTaskForm;